import React, { useMemo, useState, type ReactNode } from 'react';
import { PageErrorState } from '@/components/shared/PageErrorState';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, BookOpen, Edit, Package, Mail, Phone, MapPin, FileText, Layers, ClipboardList, Link2, RefreshCw, Loader2, Plus, X, Check } from 'lucide-react';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useSupplierMutations } from '@/features/suppliers/hooks/useSupplierMutations';
import { SupplierCategoriesCard } from '@/features/suppliers/components/SupplierCategoriesCard';
import { SupplierSkusCard } from '@/features/suppliers/components/SupplierSkusCard';
import { SupplierLeadTimesCard } from '@/features/suppliers/components/SupplierLeadTimesCard';
import { SupplierSupplyCard } from '@/features/suppliers/components/SupplierSupplyCard';
import { useOwnedFloorIds } from '@/features/inventory/hooks/useOwnedFloorIds';
import { SupplierPricingCard } from '@/features/suppliers/components/SupplierPricingCard';
import { PartnerAccessCard } from '@/features/supplier-portal/components/PartnerAccessCard';
import { CatalogLinkedPill } from '@/features/supplier-catalog/components/CatalogLinkedPill';
import { useRole } from '@/hooks/use-role';
import {
  parseAddress,
  formatAddress,
  serializeAddress,
  parseEmails,
  serializeEmails,
  COUNTRY_OPTIONS,
} from '@/features/suppliers/types';
import type { StructuredAddress } from '@/features/suppliers/types';
import { trpc } from '@/lib/trpc';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useLocation } from '@/contexts/LocationContext';
import { toast } from '@/hooks/use-toast';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar } from '@/components/layout/PageActionBar';
import { BackToTopPill } from '@/components/BackToTopPill';
import { PageLoader } from '@/components/loading/PageLoader';
import { LoadingShell } from '@/components/loading/LoadingShell';
import { updateSearchParams } from '@/lib/search-params';

// Only a supplier linked to the catalog has this tab: its own chunk.
const SupplierCatalogTab = React.lazy(() =>
  import('@/features/supplier-catalog/components/SupplierCatalogTab').then((m) => ({ default: m.SupplierCatalogTab })),
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CURRENCY_OPTIONS = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'] as const;

const toProductsInput = (products: string[]) => products.join(', ');
const parseProductsInput = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

export default function SupplierProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { updateSupplier } = useSupplierMutations();
  const { isAdmin } = useRole();
  // BLU-1506: "When to buy" is about blanks bought for a floor this org runs;
  // an org with no floor of its own (a 3PL client) never sees it.
  const runsAFloor = useOwnedFloorIds().length > 0;
  const { t } = useTranslation('inventory');
  const [searchParams, setSearchParams] = useSearchParams();

  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    contact_name: '',
    phone: '',
    default_currency: '',
    payment_terms_days: '',
    deposit_percent: '',
    minimum_order_quantity: '',
    products: '',
    description: '',
  });
  const [address, setAddress] = useState<StructuredAddress>({
    street: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia',
  });
  const [hidePricesOnPdf, setHidePricesOnPdf] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const { suppliers, isLoading, error } = useSuppliers({ includeInactive: true });
  const supplier = suppliers.find((s) => s.id === id) ?? null;
  // BLU-1268: a supplier linked to a PrintRun catalog supplier gets a Catalog tab
  // (?tab=catalog), beside its details. Editing always shows the details.
  const catalogSupplierId = supplier?.catalogSupplierId ?? null;
  const tab = catalogSupplierId && !isEditing && searchParams.get('tab') === 'catalog' ? 'catalog' : 'details';
  const changeTab = (value: string) =>
    setSearchParams((previous) => updateSearchParams(previous, { tab: value === 'catalog' ? 'catalog' : null }), {
      replace: true,
    });
  const { format: formatCurrency } = useCurrencyFormatter();
  // `allLocations`, not `locations`: the lookup below RESOLVES A NAME on
  // historical purchase orders for this supplier.
  const { allLocations } = useLocation();

  const { data: supplierOrdersData, isLoading: supplierOrdersLoading } =
    trpc.purchaseOrders.getOrdersBySupplier.useQuery(
      { supplierId: supplier?.id ?? '' },
      { enabled: !!supplier?.id }
    );

  const pushSupplierToXero = trpc.xero.pushSupplier.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.suppliers.list.invalidate(),
        utils.xero.getSyncLogs.invalidate(),
      ]);
      toast({ title: 'Supplier pushed to Xero' });
    },
    onError: (err) =>
      toast({ title: 'Failed to push supplier to Xero', description: err.message, variant: 'destructive' }),
  });

  const locationLookup = new Map(allLocations.map((loc) => [loc.id, loc.name]));
  const supplierOrders = supplierOrdersData?.orders ?? [];
  const orderCount = supplierOrdersData?.stats.orderCount ?? 0;
  const totalSpent = supplierOrdersData?.stats.totalSpent ?? 0;

  const enterEditMode = () => {
    if (!supplier) return;
    setFormData({
      title: supplier.title,
      contact_name: supplier.contact_name,
      phone: supplier.phone,
      default_currency: supplier.default_currency ?? '',
      payment_terms_days: supplier.payment_terms_days == null ? '' : String(supplier.payment_terms_days),
      deposit_percent: supplier.deposit_percent == null ? '' : String(supplier.deposit_percent),
      minimum_order_quantity: supplier.minimum_order_quantity,
      products: toProductsInput(supplier.products),
      description: supplier.description,
    });
    setAddress(parseAddress(supplier.address));
    setHidePricesOnPdf(supplier.hide_prices_on_pdf ?? false);
    setEmails(parseEmails(supplier.email));
    setEmailInput('');
    setEmailError('');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEmailInput('');
    setEmailError('');
  };

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (emails.includes(trimmed)) {
      setEmailError('This email has already been added');
      return;
    }
    setEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSave = () => {
    if (!supplier) return;

    const paymentTermsDaysParsed = formData.payment_terms_days.trim()
      ? Number.parseInt(formData.payment_terms_days.trim(), 10)
      : Number.NaN;
    const depositPercentParsed = formData.deposit_percent.trim()
      ? Number.parseInt(formData.deposit_percent.trim(), 10)
      : Number.NaN;

    // Auto-include any pending email typed in the input field
    let finalEmails = emails;
    const pendingEmail = emailInput.trim();
    if (pendingEmail && EMAIL_REGEX.test(pendingEmail) && !emails.includes(pendingEmail)) {
      finalEmails = [...emails, pendingEmail];
    }

    const payload = {
      title: formData.title.trim(),
      contact_name: formData.contact_name.trim(),
      email: serializeEmails(finalEmails),
      phone: formData.phone.trim(),
      address: serializeAddress(address),
      minimum_order_quantity: formData.minimum_order_quantity.trim(),
      products: parseProductsInput(formData.products),
      description: formData.description.trim(),
      default_currency: formData.default_currency.trim(),
      payment_terms_days: Number.isFinite(paymentTermsDaysParsed) ? paymentTermsDaysParsed : null,
      deposit_percent: Number.isFinite(depositPercentParsed) ? depositPercentParsed : null,
      hide_prices_on_pdf: hidePricesOnPdf,
    };

    updateSupplier.mutate(
      { supplierId: supplier.id, updates: payload },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  const isSaving = updateSupplier.isPending;

  // Push supplier identity into the centred TopHeader slot (back → /suppliers).
  const headerTitle = useMemo(() => {
    if (!supplier) return null;
    return (
      <span className="inline-flex items-center gap-2">
        <span className="max-w-[36ch] truncate">
          {isEditing ? formData.title || supplier.title : supplier.title}
        </span>
        <Badge variant={supplier.is_active ? 'success' : 'secondary'}>
          {supplier.is_active ? 'Active' : 'Inactive'}
        </Badge>
        {supplier.catalogSupplierId && <CatalogLinkedPill className="hidden sm:inline-flex" />}
      </span>
    );
  }, [supplier, isEditing, formData.title]);
  usePageTitle(headerTitle, null, { label: 'Back to Suppliers', to: '/suppliers' });

  if (isLoading) {
    return <PageLoader pool="supplier" />;
  }

  if (error) {
    return (
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          <PageErrorState title="Error" description={error.message} />
        </main>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center py-20 gap-4">
            <Package className="h-12 w-12 text-muted-foreground" />
            <h2 className="text-xl font-semibold">Supplier not found</h2>
            <p className="text-muted-foreground">
              The supplier you are looking for does not exist or has been removed.
            </p>
            <Button variant="outline" onClick={() => navigate('/suppliers')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Suppliers
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const parsedAddress = parseAddress(supplier.address);
  const addressStr = formatAddress(parsedAddress);
  const parsedEmails = parseEmails(supplier.email);

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        {/* "Back to Suppliers" lives in the TopHeader (typewriter button). */}
        <OptionalTabs enabled={!!catalogSupplierId} value={tab} onValueChange={changeTab}>
        <PageActionBar
          ariaLabel="Supplier actions"
          left={
            catalogSupplierId ? (
              <TabsList className="h-auto flex-wrap">
                <TabsTrigger value="details" className="flex items-center gap-1.5 text-xs">
                  <Package className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('supplierCatalog.tab.details')}
                </TabsTrigger>
                <TabsTrigger value="catalog" disabled={isEditing} className="flex items-center gap-1.5 text-xs">
                  <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('supplierCatalog.tab.catalog')}
                </TabsTrigger>
              </TabsList>
            ) : undefined
          }
          right={
            tab === 'catalog' ? undefined : isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={cancelEdit}
                  disabled={isSaving}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      Save
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={enterEditMode}
                  disabled={!supplier.is_active}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  <Edit className="h-3.5 w-3.5" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => pushSupplierToXero.mutate({ supplierId: supplier.id })}
                  disabled={!supplier.is_active || pushSupplierToXero.isPending}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  {pushSupplierToXero.isPending ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Link2 className="h-3.5 w-3.5" />
                  )}
                  {supplier.xero_contact_id ? 'Update Xero Contact' : 'Push to Xero'}
                </Button>
              </>
            )
          }
        />

        {catalogSupplierId && (
          <TabsContent value="catalog" className="mt-6">
            <React.Suspense fallback={<LoadingShell />}>
              <SupplierCatalogTab
                supplier={{ id: supplier.id, title: supplier.title, is_active: supplier.is_active, catalogSupplierId }}
              />
            </React.Suspense>
          </TabsContent>
        )}

        <OptionalTabPanel enabled={!!catalogSupplierId} value="details">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 mt-6">
          {/* Left column — supplier details + enrichment cards (Phase A) */}
          <div className="lg:col-span-2 space-y-6">
          {/* Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Supplier Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                /* ---------- EDIT MODE ---------- */
                <div className="space-y-5">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_title">Supplier Name *</Label>
                    <Input
                      id="edit_title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      disabled={isSaving}
                    />
                  </div>

                  {/* Contact */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_contact_name">Main Contact Name</Label>
                    <Input
                      id="edit_contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      disabled={isSaving}
                    />
                  </div>

                  {/* Emails */}
                  <div className="space-y-2">
                    <Label>Email Addresses</Label>
                    {emails.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {emails.map((email) => (
                          <Badge key={email} variant="secondary" className="gap-1 pr-1 text-xs">
                            {email}
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail(email)}
                              className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                              disabled={isSaving}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="name@example.com"
                        value={emailInput}
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          setEmailError('');
                        }}
                        onKeyDown={handleEmailInputKeyDown}
                        disabled={isSaving}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddEmail}
                        disabled={isSaving || !emailInput.trim()}
                        className="shrink-0"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    {emailError && (
                      <p className="text-xs text-destructive">{emailError}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_phone">Phone</Label>
                    <Input
                      id="edit_phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      disabled={isSaving}
                    />
                  </div>

                  {/* Address */}
                  <div className="space-y-3">
                    <Label>Address</Label>
                    <div className="space-y-1.5">
                      <Label htmlFor="edit_addr_street" className="text-xs text-muted-foreground">Street Address</Label>
                      <Input
                        id="edit_addr_street"
                        value={address.street}
                        onChange={(e) => setAddress({ ...address, street: e.target.value })}
                        disabled={isSaving}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="edit_addr_suburb" className="text-xs text-muted-foreground">Suburb</Label>
                        <Input
                          id="edit_addr_suburb"
                          value={address.suburb}
                          onChange={(e) => setAddress({ ...address, suburb: e.target.value })}
                          disabled={isSaving}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="edit_addr_state" className="text-xs text-muted-foreground">State</Label>
                        <Input
                          id="edit_addr_state"
                          value={address.state}
                          onChange={(e) => setAddress({ ...address, state: e.target.value })}
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="edit_addr_postcode" className="text-xs text-muted-foreground">Postcode</Label>
                        <Input
                          id="edit_addr_postcode"
                          value={address.postcode}
                          onChange={(e) => setAddress({ ...address, postcode: e.target.value })}
                          disabled={isSaving}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="edit_addr_country" className="text-xs text-muted-foreground">Country</Label>
                        <Select
                          value={address.country}
                          onValueChange={(value) => setAddress({ ...address, country: value })}
                          disabled={isSaving}
                        >
                          <SelectTrigger id="edit_addr_country">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRY_OPTIONS.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Products & MOQ */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="edit_moq">Minimum Order Quantity</Label>
                      <Input
                        id="edit_moq"
                        value={formData.minimum_order_quantity}
                        onChange={(e) => setFormData({ ...formData, minimum_order_quantity: e.target.value })}
                        placeholder="e.g., 500 or No MOQ"
                        disabled={isSaving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_products">Products Offered</Label>
                      <Input
                        id="edit_products"
                        value={formData.products}
                        onChange={(e) => setFormData({ ...formData, products: e.target.value })}
                        placeholder="Comma-separated list"
                        disabled={isSaving}
                      />
                      <p className="text-xs text-muted-foreground">Example: Hats, Socks, Stickers</p>
                    </div>
                  </div>

                  {/* Currency, Payment Terms, Deposit */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="edit_currency">Default Currency</Label>
                      <Select
                        value={formData.default_currency || '__org_default__'}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            default_currency: value === '__org_default__' ? '' : value,
                          })
                        }
                        disabled={isSaving}
                      >
                        <SelectTrigger id="edit_currency">
                          <SelectValue placeholder="Org default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__org_default__">Org default</SelectItem>
                          {CURRENCY_OPTIONS.map((currency) => (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_payment_terms">Payment Terms (days)</Label>
                      <Input
                        id="edit_payment_terms"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        placeholder="e.g., 30"
                        value={formData.payment_terms_days}
                        onChange={(e) => setFormData({ ...formData, payment_terms_days: e.target.value })}
                        disabled={isSaving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_deposit">Deposit (%)</Label>
                      <Input
                        id="edit_deposit"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        placeholder="e.g., 0"
                        value={formData.deposit_percent}
                        onChange={(e) => setFormData({ ...formData, deposit_percent: e.target.value })}
                        disabled={isSaving}
                      />
                    </div>
                  </div>

                  {/* PDF Settings */}
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <Label htmlFor="edit_hide_prices">Hide prices on PO PDFs</Label>
                      <p className="text-xs text-muted-foreground">
                        When enabled, unit costs and totals are excluded from purchase order PDFs sent to this supplier.
                      </p>
                    </div>
                    <Switch
                      id="edit_hide_prices"
                      checked={hidePricesOnPdf}
                      onCheckedChange={setHidePricesOnPdf}
                      disabled={isSaving}
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="edit_description">Notes</Label>
                    <Textarea
                      id="edit_description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              ) : (
                /* ---------- READ MODE ---------- */
                <>
                  <DetailRow icon={Package} label="Name" value={supplier.title} />
                  <Separator />
                  <DetailRow
                    icon={Mail}
                    label="Contact"
                    value={supplier.contact_name || '-'}
                  />
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-muted-foreground">Email</p>
                      {parsedEmails.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {parsedEmails.map((email) => (
                            <a
                              key={email}
                              href={`mailto:${email}`}
                              className="text-sm text-primary hover:underline"
                            >
                              {email}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm">{'-'}</p>
                      )}
                    </div>
                  </div>
                  <Separator />
                  <DetailRow
                    icon={Phone}
                    label="Phone"
                    value={supplier.phone || '-'}
                  />
                  <Separator />
                  <DetailRow
                    icon={MapPin}
                    label="Address"
                    value={addressStr || '-'}
                  />
                  <Separator />
                  <DetailRow
                    icon={Layers}
                    label="Products"
                    value={supplier.products.length > 0 ? supplier.products.join(', ') : '-'}
                  />
                  <Separator />
                  <DetailRow
                    icon={Package}
                    label="Minimum Order Quantity"
                    value={supplier.minimum_order_quantity || '-'}
                  />
                  <Separator />
                  <DetailRow
                    icon={Package}
                    label="Default Currency"
                    value={supplier.default_currency ? String(supplier.default_currency).toUpperCase() : 'Org default'}
                  />
                  <Separator />
                  <DetailRow
                    icon={Package}
                    label="Payment Terms"
                    value={
                      supplier.payment_terms_days == null
                        ? '-'
                        : `${supplier.payment_terms_days} day${supplier.payment_terms_days === 1 ? '' : 's'}`
                    }
                  />
                  <Separator />
                  <DetailRow
                    icon={Package}
                    label="Deposit %"
                    value={supplier.deposit_percent == null ? '-' : `${supplier.deposit_percent}%`}
                  />
                  <Separator />
                  <DetailRow
                    icon={FileText}
                    label="Hide Prices on PDFs"
                    value={supplier.hide_prices_on_pdf ? 'Yes' : 'No'}
                  />
                  <Separator />
                  <DetailRow
                    icon={Package}
                    label="Xero Contact"
                    value={supplier.xero_contact_id || '-'}
                  />
                  {supplier.description && (
                    <>
                      <Separator />
                      <DetailRow
                        icon={FileText}
                        label="Notes"
                        value={supplier.description}
                      />
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Phase A — supplier enrichment cards */}
          <SupplierCategoriesCard supplierId={supplier.id} canEdit={isAdmin()} />
          <SupplierSkusCard supplierId={supplier.id} canEdit={isAdmin()} />
          {/* BLU-1506: Order and wait, or Buy on print day */}
          {runsAFloor && (
            <SupplierSupplyCard
              supplierId={supplier.id}
              supplierName={supplier.title}
              catalogSupplierId={supplier.catalogSupplierId ?? null}
              initial={{
                supplyMode: supplier.supplyMode,
                sendMode: supplier.sendMode,
                sendTime: supplier.sendTime,
                leadTimeDays: supplier.lead_time_standard_days,
                followStockBelow: supplier.followStockBelow ?? null,
              }}
              canEdit={isAdmin()}
            />
          )}
          <SupplierLeadTimesCard
            supplierId={supplier.id}
            initial={{
              standard: supplier.lead_time_standard_days ?? null,
              sample: supplier.lead_time_sample_days ?? null,
              urgent: supplier.lead_time_urgent_days ?? null,
            }}
            canEdit={isAdmin()}
          />
          <SupplierPricingCard
            supplierId={supplier.id}
            defaultCurrency={supplier.default_currency ?? null}
            canEdit={isAdmin()}
          />
          </div>

          {/* Summary Stats + PO History */}
          <div className="space-y-6">
            {/* Partner portal access (admin-only; hides itself when the
                SUPPLIER_PORTAL_ENABLED flag is off) */}
            {isAdmin() && <PartnerAccessCard supplierId={supplier.id} />}

            {/* Summary Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Orders</span>
                  <span className="text-sm font-medium">{supplierOrdersLoading ? '...' : orderCount}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Spent</span>
                  <span className="text-sm font-medium">
                    {supplierOrdersLoading ? '...' : formatCurrency(totalSpent)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Purchase Order History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Purchase Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {supplierOrdersLoading ? (
                  <p className="text-sm text-muted-foreground">Loading purchase orders...</p>
                ) : supplierOrders.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No purchase orders found for this supplier.</p>
                ) : (
                  <div className="space-y-3">
                    {supplierOrders.slice(0, 8).map((po) => (
                      <Link
                        key={po.id}
                        to={`/orders/${po.id}`}
                        className="block rounded-md border p-3 hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{po.po_number ?? '-'}</div>
                            <div className="text-xs text-muted-foreground">
                              {po.location_id ? (locationLookup.get(po.location_id) ?? 'Unknown location') : 'All locations'}
                              {po.status ? ` · ${po.status.replace('_', ' ')}` : ''}
                            </div>
                          </div>
                          <div className="text-sm font-medium">
                            {po.total_cost == null ? '-' : formatCurrency(po.total_cost, po.currency ?? null)}
                          </div>
                        </div>
                      </Link>
                    ))}
                    {supplierOrders.length > 8 && (
                      <p className="text-xs text-muted-foreground">
                        Showing 8 of {supplierOrders.length}. Filter in Purchase Orders for more.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        </OptionalTabPanel>
        </OptionalTabs>
        <BackToTopPill />
      </main>
    </div>
  );
}

/** Tabs only when the supplier has more than its details to show (a linked catalog). */
function OptionalTabs({
  enabled,
  value,
  onValueChange,
  children,
}: {
  enabled: boolean;
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
}) {
  if (!enabled) return <>{children}</>;
  return (
    <Tabs value={value} onValueChange={onValueChange}>
      {children}
    </Tabs>
  );
}

function OptionalTabPanel({ enabled, value, children }: { enabled: boolean; value: string; children: ReactNode }) {
  if (!enabled) return <>{children}</>;
  return (
    <TabsContent value={value} className="mt-0">
      {children}
    </TabsContent>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  );
}
